/* @flow */

import * as React from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'

import {
  addPxToInput,
  ensureAbsoluteUrl,
  removeNonDigits,
} from '../../../../shared'
import {
  WebshopContext,
  NAVIGATION_LINK_TYPE_SEARCH,
  NAVIGATION_LINK_TYPE_PAGE,
  NAVIGATION_LINK_TYPE_BESTSELLERS,
} from '../../../shared'
import { WebshopGridItemBanner } from '../shared'

const WebshopBanner = ({ brand, gridItem, ...rest }) => {
  const { navigationLinks } = React.useContext(WebshopContext)

  const contentContainerStyle = React.useMemo(() => {
    const style = {
      height: '100%',
    }

    if (gridItem.data.inner_elements_position === 'center') {
      style.alignItems = 'center'
      style.display = 'flex'
      style.justifyContent = 'center'
    }

    return style
  }, [gridItem.data.inner_elements_position])

  const navigationLinksIdToUrlMap = React.useMemo(() => {
    return navigationLinks.reduce((acc, link) => {
      let url = ''

      if (
        link.type === NAVIGATION_LINK_TYPE_SEARCH ||
        link.type === NAVIGATION_LINK_TYPE_BESTSELLERS
      ) {
        url = `/shop/${brand.id}/search/${link.id}`
      } else if (link.type === NAVIGATION_LINK_TYPE_PAGE) {
        url = `/shop/${brand.id}/page/${link.settings.page_id}`
      }

      acc[link.id] = url
      return acc
    }, {})
  }, [navigationLinks])

  return (
    <BannerLinkWrap
      brand={brand}
      gridItem={gridItem}
      navigationLinksIdToUrlMap={navigationLinksIdToUrlMap}
    >
      <WebshopGridItemBanner
        bodyColor={gridItem.data.body_font_color}
        contentContainerStyle={contentContainerStyle}
        gridItem={gridItem}
        heightMode="fixed"
        heightValue={gridItem.data.height}
        imageOrVideo={gridItem.data.banner}
      >
        <ChildrenContainer gridItem={gridItem}>
          {gridItem.children.map(childGridItem => {
            const GridItemComponent = childGridItem.component

            if (!GridItemComponent) {
              return null
            }

            return <GridItemComponent gridItem={childGridItem} {...rest} />
          })}
        </ChildrenContainer>
      </WebshopGridItemBanner>
    </BannerLinkWrap>
  )
}

export default WebshopBanner

const ChildrenContainer = styled.div`
  display: flex;
  flex-direction: ${({ gridItem }) =>
    gridItem.data.inner_elements_stack_mode === 'stacked' ? 'column' : 'row'};
  margin-left: ${({ gridItem }) =>
    gridItem.data.inner_elements_stack_mode === 'side_by_side'
      ? addPxToInput(
          -removeNonDigits(gridItem.data.inner_elements_space_between_each)
        )
      : 0};
  position: ${({ gridItem }) =>
    gridItem.data.inner_elements_position === 'center'
      ? 'relative'
      : 'absolute'};

  ${({ gridItem }) => {
    if (gridItem.data.inner_elements_position === 'top_left') {
      return css`
        left: 0;
        top: 0;
      `
    } else if (gridItem.data.inner_elements_position === 'top_right') {
      return css`
        right: 0;
        top: 0;
      `
    } else if (gridItem.data.inner_elements_position === 'bottom_left') {
      return css`
        bottom: 0;
        left: 0;
      `
    } else if (gridItem.data.inner_elements_position === 'bottom_right') {
      return css`
        bottom: 0;
        right: 0;
      `
    }
  }};

  > * {
    margin-bottom: ${({ gridItem }) =>
      gridItem.data.inner_elements_stack_mode === 'stacked'
        ? addPxToInput(gridItem.data.inner_elements_space_between_each)
        : 0};
    margin-left: ${({ gridItem }) =>
      gridItem.data.inner_elements_stack_mode === 'side_by_side'
        ? addPxToInput(gridItem.data.inner_elements_space_between_each)
        : 0};
  }
`

const BannerLinkWrap = ({
  brand,
  children,
  gridItem,
  navigationLinksIdToUrlMap,
}) => {
  if (
    gridItem.data.link_type === 'navigation_link' &&
    gridItem.data.navigation_link_id &&
    navigationLinksIdToUrlMap[gridItem.data.navigation_link_id]
  ) {
    return (
      <Link to={navigationLinksIdToUrlMap[gridItem.data.navigation_link_id]}>
        {children}
      </Link>
    )
  }

  if (gridItem.data.link_type === 'webshop_page') {
    return (
      <Link
        to={
          gridItem.data.webshop_page_id
            ? `/shop/${brand.id}/page/${gridItem.data.webshop_page_id}`
            : ''
        }
      >
        {children}
      </Link>
    )
  }

  if (gridItem.data.link_type === 'manual_url') {
    return <a href={ensureAbsoluteUrl(gridItem.data.manual_url)}>{children}</a>
  }

  return children
}
